import React, { useEffect, useRef } from 'react';
import { useGeneralSoContext, PropertyTypeEnum, GetSoDto, PROPERTY_INFO_KEY, SoPropertyInfoInput, useCalculationInput } from 'src/common';
import { useSessionStorage } from 'src/common/hooks';
import { ApartmentPropertyInfoForm, ApartmentPropertyInfoFormData } from 'src/components/forms/apartmentPropertyInfo.form';
import { NavigationService } from 'src/common/utils/navigation.util';
import { AppRoutes } from './_router/app.routes';
import { useGetSo } from 'src/common/api/apollo/queries/useGetSo.query';
import { ContainerComponent, Flex, FormDisclaimer, LoaderSquare, NavigationButtons, StepHeader } from 'src/components';
import { FormRef } from 'src/components/forms/form';
import { Helmet } from 'react-helmet-async';

export const PropertyInfoApartmentPage: React.FC = () => {
  const formRef = useRef<FormRef>(null);
  const { so, refetch, loading } = useGetSo();
  const { soInitialData, setSoInitialData } = useGeneralSoContext();
  const { updatePropertyInfo } = useCalculationInput();
  const { setValue: setPropertyInfoSession, getValue: getPropertyInfo } = useSessionStorage<SoPropertyInfoInput>(PROPERTY_INFO_KEY);

  useEffect(() => {
    if (so) {
      refetch();
    }
  }, []);

  const handleBack = () => {
    NavigationService.navigate(AppRoutes.PROPERTY_TYPE);
  };

  const handleFormSubmit = (values: ApartmentPropertyInfoFormData) => {
    const existingPropertyInfo = so?.productTypeSpecific?.propertyInfo;

    const propertyInfo = {
      type: PropertyTypeEnum.APARTMENT,
      city: values.city || '',
      street: values.street || '',
      houseNumber: values.houseNumber || '',
      area: values.area || 0,
      floor: ['floor'],
      masonry: true,
      constructionYear: {
        exactYear: values.exactYear!,
        before1964: (values.exactYear || 1964) < 1964,
      },
      cityKompareId: values.cityKompareId || existingPropertyInfo?.cityKompareId || '',
      streetKompareId: values.streetKompareId || existingPropertyInfo?.streetKompareId || '',
      cityParentName: existingPropertyInfo?.cityParentName || '',
      zipCode: values.zipCode ? parseInt(values.zipCode, 10) : (existingPropertyInfo?.zipCode || 0),
      usage: 'permanent',
    };

    setSoInitialData({ ...soInitialData, propertyInfo });
    setPropertyInfoSession(propertyInfo);
    updatePropertyInfo({ area: propertyInfo.area, constructionYear: propertyInfo.constructionYear });
    NavigationService.navigate(AppRoutes.WEBSHOP.CUSTOMER);
  };

  if (loading) return (
    <ContainerComponent>
      <Flex align="center" justify="center" style={{ height: '40vh' }}>
        <LoaderSquare/>
      </Flex>
    </ContainerComponent>
  );

  const modelToForm = (so?: GetSoDto): ApartmentPropertyInfoFormData => {
    const propertyInfo = getPropertyInfo() ?? so?.productTypeSpecific?.propertyInfo;
    return {
      area: propertyInfo?.area || undefined,
      exactYear: propertyInfo?.constructionYear?.exactYear || undefined,
      city: propertyInfo?.city || '',
      cityKompareId: propertyInfo?.cityKompareId || undefined,
      street: propertyInfo?.street || '',
      streetKompareId: propertyInfo?.streetKompareId || undefined,
      zipCode: propertyInfo?.zipCode?.toString() || undefined,
      houseNumber: propertyInfo?.houseNumber || ''
    }
  };

  const formData = modelToForm(so || undefined);

  return (
    <ContainerComponent>
        <Helmet>
          <title>Podaci o stanu | Osiguranje imovine - kompare.hr</title>
        </Helmet>
        <StepHeader/>
        <ApartmentPropertyInfoForm
          ref={formRef}
          data={formData}
          onSubmit={handleFormSubmit}
        />
        <NavigationButtons
          handleBack={handleBack}
          handleNext={() => formRef.current?.submitForm()}
          gap={64}
        />
        <FormDisclaimer
          intro={true}
        />
    </ContainerComponent>
  );
};

export default PropertyInfoApartmentPage;
